<script lang="ts">
import { defineComponent, provide } from 'vue';
import CoordinatorProvider from './facets/Form/coordinator/CoordinatorProvider';
import FormFacet from './facets/Form/FormFacet.vue';
import UnifiedSignupFormFacet from './facets/Form/UnifiedSignupFormFacet.vue';
import ServiceEndpointsUtil from './util/ServiceEndpointsUtil';
import LocalhostTestingUtil from './util/LocalhostTestingUtil';
import AntiSpamUtil from './util/AntiSpamUtil';
import ErrorUtil from './util/ErrorUtil';
import type { ButtonPortableText } from '~/sanity/types';
import UxProfileUtil from './util/UxProfileUtil';

LocalhostTestingUtil.isEnabled(); // Will log info if enabled

type FormFortProps = {
  formId: string;
  /** The url to redirect (empty if no redirect) */
  redirectUrl: string;
  /** Google analytics conversion category (empty for default) */
  gaCategory: string;

  submitText: string;

  /** Google analytics conversion action (empty for default) */
  gaAction: string;
  /** Html string (innerHTML) to show on success (empty to show default thank you) */
  successMessagePortableText?: ButtonPortableText;

  formVariant: string;

  /** Hidden static fields for custom forms */
  hiddenStaticField1: string;
  hiddenStaticField2: string;
};

export default defineComponent({
  components: {
    FormFacet,
    UnifiedSignupFormFacet,
  },
  props: {
    formId: {
      type: String,
      required: false,
      default: '',
    },
    redirectUrl: {
      type: String,
      required: false,
      default: null,
    },
    gaCategory: {
      type: String,
      required: false,
      default: null,
    },
    submitText: {
      type: String,
      required: false,
      default: 'Submit',
    },
    gaAction: {
      type: String,
      required: false,
      default: null,
    },
    successMessagePortableText: {
      type: Object as () => ButtonPortableText | undefined,
      required: false,
      default: null,
    },
    formVariant: {
      type: String,
      required: false,
      default: '',
    },
    hiddenStaticField1: {
      type: String,
      required: false,
      default: '',
    },
    hiddenStaticField2: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props: FormFortProps) {
    const coordinator = CoordinatorProvider.init();
    try {
      AntiSpamUtil.handleTabsThatHaveBeenOpenForTooLong();
      ServiceEndpointsUtil.initListener();
      UxProfileUtil.watchInteractions();
    } catch (e) {
      ErrorUtil.internalError({ msg: 'Could not init anti-spam', error: e });
    }
    provide('formId', props.formId);
    coordinator.setup({
      formId: props.formId,
      redirectUrl: props.redirectUrl,
      gaCategory: props.gaCategory,
      gaAction: props.gaAction,
      successMessagePortableText: props.successMessagePortableText,
      submitText: props.submitText,
      formVariant: props.formVariant,
      hiddenStaticField1: props.hiddenStaticField1,
      hiddenStaticField2: props.hiddenStaticField2,
    });
    return coordinator.formApp();
  },
});
</script>

<template>
  <div
    v-if="isFormUnloadable"
    class="formfort-cannot-load"
  >
    There was a network error loading the form. This is most commonly caused by ad-blockers and
    firewalls.<br />
    <strong>You may try:</strong><br />
    <ul>
      <li><a href="">Reloading</a> the page.</li>
      <li>Disabling your ad blocker.</li>
      <li>Updating your firewall rules.</li>
      <li>Allowing all <strong>*.onshape.com</strong> connections.</li>
      <li><a href="https://www.onshape.com/en/domain-whitelisting">Domain Whitelisting</a></li>
      <li>Contacting support <strong>support@onshape.com</strong></li>
    </ul>
  </div>
  <div
    v-if="isTranslationReady && formNotValidErrorMsg"
    class="formfort-app"
  >
    <div class="form-top-error">
      <p>{{ $t(formNotValidErrorMsg, formNotValidErrorMsg) }}</p>
    </div>
  </div>
  <div
    v-if="formDefinition"
    ref="appElement"
    :key="`call-id-${callId}`"
    class="formfort-app"
  >
    <form-facet v-if="!isUnifiedSignupForm" />
    <unified-signup-form-facet v-if="isUnifiedSignupForm" />
  </div>
</template>

<style lang="scss">
@use './style/vars.scss' as vars;

.formfort-app {
  width: 100%;
}

.formfort-cannot-load {
  border: 1px solid vars.$color-error;
  background: vars.$color-error-background;
  padding: 8px;
  margin: 16px;
}

.form-top-error {
  color: vars.$color-error;
  font-style: oblique;
  width: 100%;
  font-size: 16px;
  border: 1px solid vars.$color-error;
  background: vars.$color-error-background;
  padding: 10px 0 10px 0;
  p {
    padding: 4px;
    text-align: center;
  }
}
</style>
